import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UnverifiedUser } from "@/types/unverified-user.definition";

interface UnverifiedUsersState {
  users: UnverifiedUser[];
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
}

const initialState: UnverifiedUsersState = {
  users: [],
  loading: false,
  error: null,
  lastFetch: null,
};

export const fetchUnverifiedUsersRequest = createAction(
  "unverifiedUsers/fetchRequest"
);
export const fetchUnverifiedUsersSuccess = createAction<UnverifiedUser[]>(
  "unverifiedUsers/fetchSuccess"
);
export const fetchUnverifiedUsersFailure = createAction<string>(
  "unverifiedUsers/fetchFailure"
);

export const verifyUserRequest = createAction<{ userId: number }>(
  "unverifiedUsers/verifyRequest"
);
export const verifyUserSuccess = createAction<number>(
  "unverifiedUsers/verifySuccess"
);
export const verifyUserFailure = createAction<string>(
  "unverifiedUsers/verifyFailure"
);

const unverifiedUsersSlice = createSlice({
  name: "unverifiedUsers",
  initialState,
  reducers: {
    clearUnverifiedUsersError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUnverifiedUsersRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnverifiedUsersSuccess, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.lastFetch = Date.now();
        state.error = null;
      })
      .addCase(fetchUnverifiedUsersFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(verifyUserRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyUserSuccess, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.users = state.users.filter(u => u.id !== action.payload);
        state.error = null;
      })
      .addCase(verifyUserFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUnverifiedUsersError } = unverifiedUsersSlice.actions;

export const selectUnverifiedUsers = (state: {
  unverifiedUsers: UnverifiedUsersState;
}) => state.unverifiedUsers.users;

export const selectUnverifiedUsersLoading = (state: {
  unverifiedUsers: UnverifiedUsersState;
}) => state.unverifiedUsers.loading;

export const selectUnverifiedUsersError = (state: {
  unverifiedUsers: UnverifiedUsersState;
}) => state.unverifiedUsers.error;

export const selectUnverifiedUsersIsDataStale = (state: {
  unverifiedUsers: UnverifiedUsersState;
}) => {
  if (!state.unverifiedUsers.lastFetch) return true;
  return Date.now() - state.unverifiedUsers.lastFetch > 5 * 60 * 1000;
};

export default unverifiedUsersSlice.reducer;
